import { countriesQuery } from "@/api/location";
import { plansForCountryQuery, searchSupportersQuery, useCreateSupporterMutation } from "@/api/supporter";
import { useDebouncedQuery } from "@/api/utils";
import { DataTable } from "@/components/data-table/DataTable";
import { DataTableBody } from "@/components/data-table/DataTableBody";
import { DataTableFooter } from "@/components/data-table/DataTableFooter";
import { DataTableHeader } from "@/components/data-table/DataTableHeader";
import { FieldInfo } from "@/components/forms/FieldInfo";
import { StyledLink } from "@/components/link/StyledLink";
import { Button } from "@/components/ui/Button";
import { Dialog } from "@/components/ui/Dialog";
import { DropdownMenu, DropdownMenuItem } from "@/components/ui/DropdownMenu";
import { IconButton } from "@/components/ui/IconButton";
import { PageWrapper } from "@/components/ui/PageWrapper";
import { COUNTRY_DATA } from "@/constants/countries";
import { planOptionLabel } from "@/modules/supporter-plans";
import { DeleteSupporterDialog } from "@/pages/admin/supporters/ArchiveSupporterDialog";
import { saveHref } from "@/utils/assume-role";
import { copyToClipboard } from "@/utils/clipboard";
import { seo } from "@/utils/seo";
import { Field, Flex, Label, QueryHandler, Select, toast } from "@givenwell/components";
import { Input } from "@/components/ui/Input";
import { CopyIcon, DotsIcon, RecycleBinIcon, UserIcon } from "@givenwell/core-solid-icons";
import { SupporterSearchResult } from "@givenwell/management-api";
import { colors } from "@givenwell/theme";
import { useForm, useStore } from "@tanstack/react-form";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useId, useState } from "react";
import { fallback, number, object, optional, pipe, string, toMinValue } from "valibot";

export const Route = createFileRoute("/admin/supporters/")({
  component: SupportersPage,
  validateSearch: object({
    q: fallback(optional(string(), ""), ""),
    country: fallback(optional(string()), undefined),
    page: fallback(pipe(optional(number(), 1), toMinValue(1)), 1),
    "page-size": fallback(pipe(optional(number(), 100), toMinValue(10)), 100),
  }),
  head: () => ({
    meta: seo({ title: "Supporters" }),
  }),
});

function SupportersPage() {
  const search = Route.useSearch();
  const { q, page, "page-size": pageSize, country } = search;

  const query = useDebouncedQuery(searchSupportersQuery(q, page, pageSize, country));
  const { data: countries } = useQuery(countriesQuery(false));

  const navigate = useNavigate();

  const rows = query.data?.items || [];

  const [archiveModalOpen, setArchiveModalOpen] = useState(false);
  const [targetSupporter, setTargetSupporter] = useState({} as SupporterSearchResult);

  function handleArchiveSupporter(supporter: SupporterSearchResult) {
    // The setTimeout here avoids radix bug with DropDown/Dialog combo leaving the
    // document body with a pointer-events none after closing.
    //
    // See https://github.com/radix-ui/primitives/issues/1241#issuecomment-1815171670
    setTimeout(() => {
      setTargetSupporter(supporter);
      setArchiveModalOpen(true);
    }, 0);
  }

  function handleDeleteDismissed() {
    setTargetSupporter({} as SupporterSearchResult);
    setArchiveModalOpen(false);
  }

  return (
    <PageWrapper
      title="Supporters"
      rightContent={
        <>
          <CreateSupporterModal />
        </>
      }
    >
      <DeleteSupporterDialog
        supporter={targetSupporter}
        isOpen={archiveModalOpen}
        onDismissed={handleDeleteDismissed}
        key={targetSupporter?.id}
      />
      <DataTable>
        <DataTableHeader hideToggle autoFocusSearch>
          <Select
            size="sm"
            css={{ width: 200 }}
            value={country || ""}
            onValueChange={country => {
              navigate({
                to: ".",
                search: {
                  ...search,
                  country: country || undefined,
                },
              });
            }}
          >
            <option value="">Any country</option>
            {countries?.countries
              .filter(country => country.capabilities.supporters_allowed)
              .map(country => (
                <option key={country.code} value={country.code}>
                  {country.name}
                </option>
              ))}
          </Select>
        </DataTableHeader>
        <DataTableBody
          data={rows}
          status={query.status}
          dataKey="id"
          columns={[
            {
              header: "Name",
              cell: supporter => (
                <StyledLink
                  to="/admin/supporters/$supporterId"
                  params={{
                    supporterId: supporter.id,
                  }}
                >
                  {supporter.name}
                </StyledLink>
              ),
            },
            {
              header: "Country",
              cell: "country_code",
            },
            {
              header: "",
              css: { align: "right" },
              cell: supporter => (
                <>
                  <DropdownMenu
                    align="end"
                    content={
                      <>
                        <DropdownMenuItem
                          onSelect={() => {
                            saveHref();
                            navigate({
                              to: "/supporter/$supporterId",
                              params: {
                                supporterId: supporter.id,
                              },
                            });
                          }}
                          icon={<UserIcon />}
                        >
                          Assume role
                        </DropdownMenuItem>
                        <DropdownMenuItem onSelect={() => copyToClipboard(supporter.id)} icon={<CopyIcon />}>
                          Copy supporter ID
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          color="red"
                          icon={<RecycleBinIcon />}
                          onSelect={() => handleArchiveSupporter(supporter)}
                        >
                          Archive supporter
                        </DropdownMenuItem>
                      </>
                    }
                  >
                    <IconButton aria-label="Actions" variant="plain" size="sm" color="neutral">
                      <DotsIcon style={{ color: colors.text2 }} />
                    </IconButton>
                  </DropdownMenu>
                </>
              ),
            },
          ]}
        />
        <DataTableFooter itemCount={query.data?.total_items} />
      </DataTable>
    </PageWrapper>
  );
}

function CreateSupporterModal() {
  const query = useQuery(countriesQuery(false));
  const navigate = useNavigate();
  const id = useId();

  const createMut = useCreateSupporterMutation();

  const form = useForm({
    defaultValues: {
      name: "",
      countryCode: "NZ",
      planId: "standard",
      defaultAllowance: 50,
    },
    onSubmit({ value }) {
      createMut.mutate(
        { name: value.name, country_code: value.countryCode, plan_id: value.planId },
        {
          onSuccess(data) {
            toast.success(`Supporter created!`);
            navigate({
              to: "/admin/supporters/$supporterId",
              params: {
                supporterId: data.id,
              },
            });
          },
        },
      );
    },
  });

  // Plans are priced in the home country's currency, so the list follows the country picked above.
  const countryCode = useStore(form.store, state => state.values.countryCode);
  const plansQuery = useQuery(plansForCountryQuery(countryCode));
  const currencyCode = COUNTRY_DATA[countryCode]?.currencyLower;

  return (
    <Dialog
      title="Create a supporter"
      content={
        <QueryHandler
          query={query}
          success={({ data }) => (
            <form
              id={id}
              onSubmit={e => {
                e.preventDefault();
                form.handleSubmit();
              }}
            >
              <Flex css={{ gap: 24, flexDir: "column", items: "center" }}>
                <form.Field
                  name="name"
                  validators={{
                    onChange: field => (!field.value.trim() ? "Name is required" : undefined),
                  }}
                >
                  {field => (
                    <Field>
                      <Label>Supporter Name</Label>
                      <Input
                        autoFocus
                        value={field.state.value}
                        onValueChange={field.handleChange}
                        onBlur={field.handleBlur}
                        className="ph-clear"
                      />
                      <FieldInfo field={field} />
                    </Field>
                  )}
                </form.Field>
                <form.Field
                  name="countryCode"
                  validators={{
                    onChange: field => (!field.value.trim() ? "Country is required" : undefined),
                  }}
                >
                  {field => (
                    <Field>
                      <Label>Country</Label>
                      <Select value={field.state.value} onValueChange={field.handleChange} onBlur={field.handleBlur}>
                        {data.countries
                          .filter(c => c.capabilities.supporters_allowed)
                          .map(country => (
                            <option key={country.code} value={country.code}>
                              {country.name}
                            </option>
                          ))}
                      </Select>
                      <FieldInfo field={field} />
                    </Field>
                  )}
                </form.Field>
                <form.Field
                  name="planId"
                  validators={{
                    onChange: field => (!field.value ? "Plan is required" : undefined),
                  }}
                >
                  {field => (
                    <Field>
                      <Label>Plan</Label>
                      <Select value={field.state.value} onValueChange={field.handleChange} onBlur={field.handleBlur}>
                        {plansQuery.data?.plans.map(plan => (
                          // A plan whose feature set does not exist yet is listed so the pricing on offer
                          // is visible, but cannot be chosen.
                          <option key={plan.plan_id} value={plan.plan_id} disabled={!plan.is_selectable}>
                            {planOptionLabel(plan, currencyCode)}
                          </option>
                        ))}
                      </Select>
                      <FieldInfo field={field} />
                    </Field>
                  )}
                </form.Field>
              </Flex>
            </form>
          )}
        />
      }
      footer={
        <form.Subscribe
          selector={({ canSubmit }) => ({ canSubmit })}
          children={({ canSubmit }) => (
            <Button
              type="submit"
              form={id}
              color="primary"
              variant="tonal"
              loading={createMut.isPending}
              disabled={!canSubmit}
              loadingText="Creating..."
            >
              Create
            </Button>
          )}
        />
      }
    >
      <Button variant="tonal" color="primary">
        Create a supporter
      </Button>
    </Dialog>
  );
}
